import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import head from 'lodash/head';
import { all, call, put, select, take, fork, cancel, takeLatest } from 'redux-saga/effects';
import {
  updateSoundcastEpisodesOrder,
  deleteManagedSoundcastEpisodes,
  bulkActionEpisodes,
} from '../routines';
import {
  updateEpisodeMetadata,
  arrangeEpisodesOrder,
  updateEpisodesOrder,
  deleteEpisode,
  deleteEpisodes,
  bulkEpisodes,
} from '../managers/episode';
import { updateSoundcastFeed } from '../managers/soundcast';
import { getUser } from '../selectors/user';
import { getSoundcastEpisodes } from '../selectors/soundcast';
const { assignEpisodeOldProps } = require('../../server/scripts/dataTransition');

const takeLatesWithSoundcast = (pattern, saga, ...args) =>
  fork(function*() {
    let lastTask;
    let lastSoundcastId;
    while (true) {
      const action = yield take(pattern);
      const targetSoundcastId = get(action, 'payload.soundcast.id', '');

      // cancel task when lastTask and lastSoundcast are the same
      if (lastTask && isEqual(lastSoundcastId, targetSoundcastId)) {
        yield cancel(lastTask);
      }

      // identify soundcast, so that it only take latest effect from soundcast scope
      lastSoundcastId = targetSoundcastId;
      lastTask = yield fork(saga, ...args.concat(action));
    }
  });

const updateEpisodesOrderWorker = function*({
  payload: { soundcast, episodes = {}, feedUpdate = true },
}) {
  const { id: soundcastId, podcastFeedVersion } = soundcast;
  const payload = { soundcastId };
  try {
    yield put(updateSoundcastEpisodesOrder.request(payload));

    const nepisodes = !isEmpty(episodes)
      ? episodes
      : yield select(getSoundcastEpisodes, soundcastId);
    const episodeIndexes = yield call(arrangeEpisodesOrder, nepisodes);

    // do not update
    if (isEmpty(episodeIndexes)) {
      throw new Error('No tracks to update');
    }

    yield put(updateSoundcastEpisodesOrder.success({ ...payload, episodes: episodeIndexes }));

    // update episode indexes and feed in the background
    // also not to trigger any busy loading state
    yield fork(function*() {
      yield call(updateEpisodesOrder, soundcast, episodeIndexes);

      // update feed whenever possible
      if (podcastFeedVersion && feedUpdate) {
        const user = yield select(getUser);
        yield call(updateSoundcastFeed, soundcast, user);
      }
    });
  } catch (error) {
    yield put(updateSoundcastEpisodesOrder.failure(payload, error));
  } finally {
    yield put(updateSoundcastEpisodesOrder.fulfill(payload));
  }
};

const updateEpisodesOrderWatcher = function*() {
  yield takeLatesWithSoundcast(updateSoundcastEpisodesOrder.TRIGGER, updateEpisodesOrderWorker);
};

const deleteManagedSoundcastEpisodesWorker = function* ({
  payload: { soundcast, episodes = [], silent = false, cancellable = true },
}) {
  const { id: soundcastId, podcastFeedVersion } = soundcast;
  const episodeIds = episodes.map(({ episodeId }) => episodeId);
  const payload = { soundcastId, episodes: episodeIds, silent };

  try {
    yield put(deleteManagedSoundcastEpisodes.request(payload));

    // reupdate feed if the soundcast has a feed and  the episode is public
    // only available if the soundcast has been submitted as a podcast;
    if (!silent && podcastFeedVersion) {
      yield all(
        episodes
          .filter(({ publicEpisode = false }) => publicEpisode)
          .map(({ episodeId }) => {
            return call(updateEpisodeMetadata, episodeId, {
              id3Tagged: false,
              isRemoved: true,
            });
          })
      );

      const user = yield select(getUser);
      yield call(updateSoundcastFeed, soundcast, user);
    }

    // call the delete episode api
    if (episodes.length === 1) {
      yield call(deleteEpisode, { soundcastId, episodeId: head(episodeIds) }, cancellable);
    } else {
      yield call(deleteEpisodes, payload);
    }

    // mark action finish
    yield put(deleteManagedSoundcastEpisodes.success(payload));
  } catch (error) {
    yield put(deleteManagedSoundcastEpisodes.failure(payload, error));
  } finally {
    yield put(deleteManagedSoundcastEpisodes.fulfill(payload));

    // update episodes order in background
    if (silent) return;
    yield fork(function* () {
      yield put(updateSoundcastEpisodesOrder({ soundcast, feedUpdate: false }));
    });
  }
};

const deleteManagedSoundcastEpisodesWatcher = function*() {
  yield takeLatest(deleteManagedSoundcastEpisodes.TRIGGER, deleteManagedSoundcastEpisodesWorker);
};

const bulkActionEpisodesWorker = function*({ payload: { soundcast, replicateToSoundcastId, episodes = [], action = '' } }) {
  const acceptedBulkActions = ['publish', 'draft', 'public', 'private', 'replicate'];
  const { id: soundcastId } = soundcast;
  const episodeIds = episodes.map(({ episodeId }) => episodeId);
  const payload = { soundcastId, episodes: episodeIds, replicateToSoundcastId };

  try {
    if (!acceptedBulkActions.includes(action)) {
      throw new Error(`Bulk action ${action} is not accepted`);
    }

    yield put(bulkActionEpisodes.request(payload));

    // call bulk actions api
    const { data: source } = yield call(bulkEpisodes, action, payload);    
    const episodes = action === 'replicate'? [] : source.reduce((prev, episode) => {
      prev[episode.episodeId] = assignEpisodeOldProps(episode);
      return prev;
    }, {});

    yield put(bulkActionEpisodes.success({ soundcastId, episodes }));
  
  } catch (error) {
    yield put(bulkActionEpisodes.failure(payload, error));
  } finally {
    yield put(bulkActionEpisodes.fulfill(payload));
  }
};

const bulkActionEpisodesWatcher = function*() {
  yield takeLatest(bulkActionEpisodes.TRIGGER, bulkActionEpisodesWorker);
};

export default function*() {
  yield all([
    call(updateEpisodesOrderWatcher),
    call(deleteManagedSoundcastEpisodesWatcher),
    call(bulkActionEpisodesWatcher),
  ]);
}
